// User Timing marks for the data-loading path (docs/audit/performance.md; read by e2e/perf.mjs).
// `vu:<name>` marks and measures cost next to nothing and show up in a browser's performance
// panel, so a slow start on a phone can be read off a trace. Never throws.

type Perf = Pick<Performance, 'mark' | 'measure' | 'now'>;
const perf: Perf | null = typeof performance !== 'undefined' && typeof performance.mark === 'function' ? performance : null;

/** A point in time: `vu:<name>` (the first one counts; later ones are ignored). */
const marked = new Set<string>();
export function perfMark(name: string): void {
  if (!perf || marked.has(name)) return;
  marked.add(name);
  try {
    perf.mark(`vu:${name}`);
  } catch {
    /* no User Timing */
  }
}

/** Time an async step as the measure `vu:<name>`. */
export async function timed<T>(name: string, fn: () => Promise<T>): Promise<T> {
  const start = perf?.now() ?? 0;
  try {
    return await fn();
  } finally {
    measure(name, start);
  }
}

/** Time a synchronous step as the measure `vu:<name>`. */
export function timedSync<T>(name: string, fn: () => T): T {
  const start = perf?.now() ?? 0;
  try {
    return fn();
  } finally {
    measure(name, start);
  }
}

function measure(name: string, start: number): void {
  if (!perf) return;
  try {
    perf.measure(`vu:${name}`, { start, end: perf.now() });
  } catch {
    /* no User Timing Level 3 */
  }
}
